import { Contributor, ContributorObj } from "types/globals";

export const transition = (value: string, ...properties: string[]) =>
  properties.map((property) => `${property} ${value}`).join(", ");

export const isArray = (value: any) => Array.isArray(value);

export const isObject = (value: any) =>
  value != null && typeof value === "object" && !isArray(value);

export const objectFilter = (object, fn) => {
  const result = {};

  Object.keys(object).forEach((key) => {
    const value = object[key];
    const shouldPass = fn(value, key, object);
    if (shouldPass) {
      result[key] = value;
    }
  });

  return result;
};

export const filterUndefined = (object) =>
  objectFilter(object, (val) => val !== null && val !== undefined);

export const mapResponsive = (prop, mapper) => {
  if (isArray(prop)) {
    return prop.map((item) => {
      if (item === null) {
        return null;
      }
      return mapper(item);
    });
  }

  if (isObject(prop)) {
    return Object.keys(prop).reduce((result, key) => {
      result[key] = mapper(prop[key]);
      return result;
    }, {});
  }

  if (prop != null) {
    return mapper(prop);
  }

  return null;
};

export const objectToGetParams = (object) => {
  const params = Object.entries(object)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
    );

  return params.length > 0 ? `?${params.join("&")}` : "";
};

export const formatDate = (date) => {
  if (!date) return "";
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const d = new Date(date);

  return `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
};

/*
 Extracts contributors into an object with arrays for each type
*/
export const extractContributors = (
  contributors: Contributor[]
): ContributorObj => {
  const contributorObj: ContributorObj = {
    authors: [],
    illustrators: [],
    coauthors: [],
    authorIllustrators: [],
    coauthorIllustrators: [],
  };

  contributors.forEach((contributor) => {
    switch (contributor.contributorType) {
      case "author":
        contributorObj.authors.push(contributor);
        break;
      case "illustrator":
        contributorObj.illustrators.push(contributor);
        break;
      case "authorIllustrator":
        contributorObj.authorIllustrators.push(contributor);
        break;
      case "coauthor":
        contributorObj.coauthors.push(contributor);
        break;
      case "coauthorIllustrator":
        contributorObj.coauthorIllustrators.push(contributor);
        break;
      default:
        console.warn("Invalid contributor type");
    }
  });

  return contributorObj;
};

/* 
  Parses contributor Object keys to display their title on Article
*/
export const parseContributorTitles = (
  key: string,
  contributorArr: Contributor[]
) => {
  let name = "";

  switch (key) {
    case "authors":
      name = "Author";
      break;
    case "illustrators":
      name = "Illustrator";
      break;
    case "authorIllustrators":
      name = "Author and illustrator";
      break;
    case "coauthors":
      name = "Co-author";
      break;
    case "coauthorIllustrators":
      name = "Co-author and illustrator";
      break;
    default:
      return "";
  }

  return contributorArr.length > 1 ? `${name}s` : name;
};
